import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import {
  fetchAuditLog,
  restoreAuditLogEntry,
  restoreAuditLogGroup,
} from "../api/adminApi";
import { theme } from "../theme";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import Button from "../components/Button";

const Container = styled.div`
  font-family: ${theme.fonts.body};
`;

const DayGroup = styled.div`
  margin-bottom: 24px;
`;

const DayHeader = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.text};
  margin: 0 0 12px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid ${theme.colors.border};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  background: ${theme.colors.surfaceSecondary};
  color: ${theme.colors.textSecondary};
  font-weight: 600;
  border-bottom: 1px solid ${theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid ${theme.colors.borderLight};
  color: ${theme.colors.text};
  vertical-align: top;
`;

const OperationBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &.CREATE {
    background: #d4edda;
    color: #155724;
  }
  &.UPDATE {
    background: #cce5ff;
    color: #004085;
  }
  &.DELETE {
    background: #f8d7da;
    color: #721c24;
  }
`;

const EntityBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: ${theme.colors.surfaceSecondary};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;
`;

const DescriptionCell = styled.div`
  max-width: 400px;
  font-size: 13px;
  line-height: 1.4;
`;

const DiffBlock = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-family: monospace;
`;

const DiffOld = styled.div`
  color: #721c24;
  background: #f8d7da;
  padding: 2px 6px;
  border-radius: 3px;
  margin-bottom: 2px;
`;

const DiffNew = styled.div`
  color: #155724;
  background: #d4edda;
  padding: 2px 6px;
  border-radius: 3px;
`;

const Loading = styled.p`
  color: ${theme.colors.textSecondary};
  font-family: ${theme.fonts.body};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: ${theme.colors.textSecondary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
  align-items: center;
`;

const Tooltip = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.text};
  color: ${theme.colors.surface};
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  margin-bottom: 6px;
  z-index: 1000;
  transition: opacity 0.2s;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: ${theme.colors.text};
  }
`;

const RestoreButtonWrapper = styled.div`
  position: relative;
  display: inline-block;

  &:hover ${Tooltip} {
    visibility: visible;
    opacity: 1;
  }
`;

const AuditLog = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const toast = useToast();
  const confirm = useConfirm();

  const LIMIT = 100;

  const loadAuditLog = useCallback(
    async (targetOffset = 0, append = false) => {
      try {
        setLoading(true);
        const data = await fetchAuditLog(LIMIT, targetOffset);
        const nextItems = data.items || [];
        setItems((prev) => {
          if (!append) return nextItems;
          const seen = new Set(prev.map((item) => item.id));
          return [...prev, ...nextItems.filter((item) => !seen.has(item.id))];
        });
        setTotal(data.total || 0);
        setHasMore(data.has_more || false);
      } catch (err) {
        toast.error("Ошибка загрузки журнала");
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadAuditLog(offset, offset > 0);
  }, [loadAuditLog, offset]);

  const handleRestore = async (entries, isGroup = false) => {
    const entryList = isGroup ? entries : [entries];

    const entityNames = {
      papers: "тип бумаги",
      blocks: "узор",
      images: "изображение",
      lists: "список",
      block_images: "связь блок-изображение",
      block_lists: "связь блок-список",
    };

    let message;
    if (isGroup) {
      const entityTypes = [...new Set(entryList.map((e) => e.entity_type))];
      const names = entityTypes.map((t) => entityNames[t] || t);
      message = `Вы собираетесь откатить ${entryList.length} связанных изменений (${names.join(", ")}).`;
    } else {
      const entry = entryList[0];
      const operationText = {
        CREATE: "создание новой записи",
        UPDATE: "обновление данных",
        DELETE: "удаление записи",
      };
      const entityName = entityNames[entry.entity_type] || entry.entity_type;
      message = `Вы собираетесь отменить ${operationText[entry.operation] || "изменение"} для ${entityName} (ID: ${entry.entity_id}).`;
    }

    const confirmed = await confirm({
      title: "Восстановить изменение?",
      message,
    });

    if (!confirmed) return;

    try {
      setRestoringId(isGroup ? entryList[0].id : entries.id);

      if (isGroup) {
        const changeGroupId =
          entryList[0].change_group_id ||
          entryList.find((e) => e.change_group_id)?.change_group_id;
        if (!changeGroupId) {
          throw new Error("Группа изменений не найдена");
        }
        await restoreAuditLogGroup(changeGroupId);
      } else {
        await restoreAuditLogEntry(entries.id);
      }

      toast.success("Изменение восстановлено");
      if (offset === 0) {
        loadAuditLog(0, false);
      } else {
        setOffset(0);
      }
    } catch (err) {
      if (err.statusCode === 409) {
        toast.error(
          err.message || "Нельзя откатить: есть более поздние изменения",
        );
      } else {
        toast.error(
          "Ошибка восстановления: " + (err.message || "неизвестная ошибка"),
        );
      }
    } finally {
      setRestoringId(null);
    }
  };

  const handleLoadMore = () => {
    setOffset((prev) => prev + LIMIT);
  };

  const handleReset = () => {
    if (offset === 0) {
      loadAuditLog(0, false);
    } else {
      setOffset(0);
    }
  };

  // Group by day, then by change_group_id within each day
  const grouped = items.reduce((groups, item) => {
    const date = new Date(item.changed_at).toLocaleDateString("ru-RU");
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(item);
    return groups;
  }, {});

  // Group entries within each day by change_group_id
  // Returns an array of display items: either a single entry or a group of entries
  function groupByChangeGroupId(entries) {
    const groupMap = new Map();
    const standalone = [];

    entries.forEach((entry) => {
      if (entry.change_group_id) {
        if (!groupMap.has(entry.change_group_id)) {
          groupMap.set(entry.change_group_id, []);
        }
        groupMap.get(entry.change_group_id).push(entry);
      } else {
        standalone.push(entry);
      }
    });

    // Build display items: groups with multiple entries become one row
    const displayItems = [];
    groupMap.forEach((groupEntries, groupId) => {
      if (groupEntries.length > 1) {
        // Multiple entries with the same change_group_id — display as one grouped row
        displayItems.push({
          type: "group",
          changeGroupId: groupId,
          entries: groupEntries,
        });
      } else {
        // Single entry with change_group_id — display normally
        displayItems.push({
          type: "single",
          entry: groupEntries[0],
        });
      }
    });

    // Add standalone entries (no change_group_id)
    standalone.forEach((entry) => {
      displayItems.push({ type: "single", entry });
    });

    // Sort by the earliest changed_at in each display item
    displayItems.sort((a, b) => {
      const aTime =
        a.type === "group"
          ? Math.min(...a.entries.map((e) => new Date(e.changed_at).getTime()))
          : new Date(a.entry.changed_at).getTime();
      const bTime =
        b.type === "group"
          ? Math.min(...b.entries.map((e) => new Date(e.changed_at).getTime()))
          : new Date(b.entry.changed_at).getTime();
      return bTime - aTime; // Descending order
    });

    return displayItems;
  }

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatActor = (entry) =>
    entry.actor_name || entry.actor_username || entry.actor_sub || "—";

  const formatGroupActor = (entries) => {
    const actors = [
      ...new Set(entries.map((entry) => formatActor(entry)).filter((a) => a !== "—")),
    ];
    return actors.length > 0 ? actors.join(", ") : "—";
  };

  const formatDescription = (entry) => {
    const entityNames = {
      papers: "Тип бумаги",
      blocks: "Узор",
      images: "Изображение",
      lists: "Список",
      block_images: "Связь блок-изображение",
      block_lists: "Связь блок-список",
    };

    const entityName = entityNames[entry.entity_type] || entry.entity_type;
    const operationNames = {
      CREATE: "Создание",
      UPDATE: "Обновление",
      DELETE: "Удаление",
    };

    const operationName = operationNames[entry.operation] || entry.operation;

    // Relations store full snapshots in old_values/new_values
    const relationKey =
      entry.entity_type === "block_images"
        ? "image_id"
        : entry.entity_type === "block_lists"
          ? "list_id"
          : null;
    if (relationKey) {
      const oldIds = Array.isArray(entry.old_values)
        ? entry.old_values.map((rel) => rel[relationKey])
        : [];
      const newIds = Array.isArray(entry.new_values)
        ? entry.new_values.map((rel) => rel[relationKey])
        : [];
      const ids = entry.operation === "DELETE" ? oldIds : newIds;
      return (
        <DescriptionCell>
          {operationName} {entityName} (ID: {entry.entity_id})
          {ids.length > 0 && (
            <div style={{ marginTop: "2px" }}>№ {ids.join(", ")}</div>
          )}
        </DescriptionCell>
      );
    }

    if (entry.operation === "UPDATE" && entry.old_values && entry.new_values) {
      // Show diff for UPDATE
      const oldKeys = Object.keys(entry.old_values);
      const newKeys = Object.keys(entry.new_values);
      const allKeys = [...new Set([...oldKeys, ...newKeys])];

      const changes = allKeys
        .filter((key) => {
          const oldVal = entry.old_values[key];
          const newVal = entry.new_values[key];
          return oldVal !== newVal;
        })
        .map((key) => ({
          key,
          old: entry.old_values[key],
          new: entry.new_values[key],
        }));

      if (changes.length === 0) {
        return `${operationName} ${entityName} (ID: ${entry.entity_id})`;
      }

      return (
        <DescriptionCell>
          <span>
            {operationName} {entityName} (ID: {entry.entity_id})
          </span>
          <DiffBlock>
            {changes.map((change, idx) => (
              <div key={idx}>
                {change.old !== undefined && (
                  <DiffOld>
                    - {change.key}: {String(change.old)}
                  </DiffOld>
                )}
                {change.new !== undefined && (
                  <DiffNew>
                    + {change.key}: {String(change.new)}
                  </DiffNew>
                )}
              </div>
            ))}
          </DiffBlock>
        </DescriptionCell>
      );
    }

    if (entry.operation === "CREATE" && entry.new_values) {
      const title =
        entry.new_values.title ||
        entry.new_values.name ||
        entry.new_values.original;
      return (
        <DescriptionCell>
          {operationName} {entityName} (ID: {entry.entity_id})
          {title && <div style={{ marginTop: "2px" }}>{title}</div>}
        </DescriptionCell>
      );
    }

    if (entry.operation === "DELETE" && entry.old_values) {
      const title =
        entry.old_values.title ||
        entry.old_values.name ||
        entry.old_values.original;
      return (
        <DescriptionCell>
          {operationName} {entityName} (ID: {entry.entity_id})
          {title && <div style={{ marginTop: "2px" }}>{title}</div>}
        </DescriptionCell>
      );
    }

    return `${operationName} ${entityName} (ID: ${entry.entity_id})`;
  };

  // Format a grouped description for entries with the same change_group_id
  const formatGroupDescription = (entries) => {
    const entityNames = {
      papers: "тип бумаги",
      blocks: "узор",
      images: "изображение",
      lists: "список",
      block_images: "связь блок-изображение",
      block_lists: "связь блок-список",
    };

    // Count operations and entity types
    const operationCounts = {};
    const entityTypes = new Set();
    entries.forEach((e) => {
      operationCounts[e.operation] = (operationCounts[e.operation] || 0) + 1;
      entityTypes.add(e.entity_type);
    });

    // Build a summary like "Удалён узор и 2 связанных изображения"
    const mainEntry =
      entries.find(
        (e) => !["block_images", "block_lists"].includes(e.entity_type),
      ) || entries[0];
    const mainEntityName =
      entityNames[mainEntry.entity_type] || mainEntry.entity_type;
    const mainOperation = mainEntry.operation;

    const operationWords = {
      CREATE: { past: "Создан", adj: "создано" },
      UPDATE: { past: "Обновлён", adj: "обновлено" },
      DELETE: { past: "Удалён", adj: "удалено" },
    };
    const opWords = operationWords[mainOperation] || {
      past: "Изменён",
      adj: "изменено",
    };

    let description = `${opWords.past} ${mainEntityName}`;

    // Add related entities info
    const relatedEntries = entries.filter((e) =>
      ["block_images", "block_lists"].includes(e.entity_type),
    );
    if (relatedEntries.length > 0) {
      const relatedParts = [];
      relatedEntries.forEach((e) => {
        const relName = entityNames[e.entity_type] || e.entity_type;
        const count =
          e.old_values && Array.isArray(e.old_values) ? e.old_values.length : 1;
        relatedParts.push(`${count} ${relName}`);
      });
      description += ` и ${relatedParts.join(", ")}`;
    }

    return (
      <DescriptionCell>
        <span>{description}</span>
        <div
          style={{
            marginTop: "2px",
            fontSize: "12px",
            color: theme.colors.textSecondary,
          }}
        >
          {entries.length} связанных записей
        </div>
      </DescriptionCell>
    );
  };

  // Check if a group can be restored (all entries must be restorable)
  const canGroupRestore = (entries) => {
    return entries.every((e) => e.can_restore);
  };

  // Get the restore blocked reason for a group
  const getGroupRestoreReason = (entries) => {
    const blocked = entries.find((e) => !e.can_restore);
    return blocked ? blocked.restore_blocked_reason : null;
  };

  if (loading && offset === 0) return <Loading>Загрузка журнала...</Loading>;

  if (items.length === 0) {
    return (
      <EmptyState>
        <p>Журнал изменений пуст</p>
        <p style={{ fontSize: "13px", marginTop: "8px" }}>
          Здесь будут отображаться все изменения, сделанные в админке
        </p>
      </EmptyState>
    );
  }

  return (
    <Container>
      {Object.entries(grouped).map(([date, entries]) => (
        <DayGroup key={date}>
          <DayHeader>{date}</DayHeader>
          <Table>
            <thead>
              <tr>
                <Th>Время</Th>
                <Th>Автор</Th>
                <Th>Сущность</Th>
                <Th>Операция</Th>
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    background: theme.colors.surfaceSecondary,
                    color: theme.colors.textSecondary,
                    fontWeight: 600,
                    borderBottom: `1px solid ${theme.colors.border}`,
                  }}
                >
                  Описание
                </th>
                <Th>Действие</Th>
              </tr>
            </thead>
            <tbody>
              {groupByChangeGroupId(entries).map((displayItem, idx) => {
                if (displayItem.type === "group") {
                  // Render a grouped row
                  const { entries: groupEntries } = displayItem;
                  const groupCanRestore = canGroupRestore(groupEntries);
                  const groupRestoreReason =
                    getGroupRestoreReason(groupEntries);
                  const isRestoring = groupEntries.some(
                    (e) => restoringId === e.id,
                  );
                  const earliestTime = Math.min(
                    ...groupEntries.map((e) =>
                      new Date(e.changed_at).getTime(),
                    ),
                  );
                  const entityTypes = [
                    ...new Set(groupEntries.map((e) => e.entity_type)),
                  ];
                  const operations = [
                    ...new Set(groupEntries.map((e) => e.operation)),
                  ];

                  return (
                    <tr key={`group-${displayItem.changeGroupId}`}>
                      <Td style={{ whiteSpace: "nowrap" }}>
                        {formatTime(new Date(earliestTime).toISOString())}
                      </Td>
                      <Td>{formatGroupActor(groupEntries)}</Td>
                      <Td>
                        {entityTypes.map((et, i) => (
                          <span key={et}>
                            {i > 0 && ", "}
                            <EntityBadge>{et}</EntityBadge>
                          </span>
                        ))}
                      </Td>
                      <Td>
                        {operations.map((op, i) => (
                          <span key={op}>
                            {i > 0 && ", "}
                            <OperationBadge className={op}>{op}</OperationBadge>
                          </span>
                        ))}
                      </Td>
                      <Td>{formatGroupDescription(groupEntries)}</Td>
                      <Td style={{ whiteSpace: "nowrap" }}>
                        <RestoreButtonWrapper>
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() =>
                              groupCanRestore &&
                              handleRestore(groupEntries, true)
                            }
                            disabled={isRestoring || !groupCanRestore}
                            title={groupRestoreReason || undefined}
                          >
                            {isRestoring ? "..." : "Восстановить"}
                          </Button>
                          {!groupCanRestore && groupRestoreReason && (
                            <Tooltip>{groupRestoreReason}</Tooltip>
                          )}
                        </RestoreButtonWrapper>
                      </Td>
                    </tr>
                  );
                } else {
                  // Render a single entry row
                  const entry = displayItem.entry;
                  return (
                    <tr key={entry.id}>
                      <Td style={{ whiteSpace: "nowrap" }}>
                        {formatTime(entry.changed_at)}
                      </Td>
                      <Td>{formatActor(entry)}</Td>
                      <Td>
                        <EntityBadge>{entry.entity_type}</EntityBadge>
                        <span
                          style={{
                            marginLeft: "6px",
                            fontSize: "12px",
                            color: theme.colors.textSecondary,
                          }}
                        >
                          #{entry.entity_id}
                        </span>
                      </Td>
                      <Td>
                        <OperationBadge className={entry.operation}>
                          {entry.operation}
                        </OperationBadge>
                      </Td>
                      <Td>{formatDescription(entry)}</Td>
                      <Td style={{ whiteSpace: "nowrap" }}>
                        {!entry.restored && (
                          <RestoreButtonWrapper>
                            <Button
                              $variant="ghost"
                              $size="sm"
                              onClick={() =>
                                entry.can_restore && handleRestore(entry)
                              }
                              disabled={
                                restoringId === entry.id || !entry.can_restore
                              }
                              title={entry.restore_blocked_reason || undefined}
                            >
                              {restoringId === entry.id
                                ? "..."
                                : "Восстановить"}
                            </Button>
                            {!entry.can_restore &&
                              entry.restore_blocked_reason && (
                                <Tooltip>
                                  {entry.restore_blocked_reason}
                                </Tooltip>
                              )}
                          </RestoreButtonWrapper>
                        )}
                      </Td>
                    </tr>
                  );
                }
              })}
            </tbody>
          </Table>
        </DayGroup>
      ))}

      <Pagination>
        {offset > 0 && (
          <Button $variant="ghost" $size="sm" onClick={handleReset}>
            ← Сначала
          </Button>
        )}
        {hasMore && (
          <Button $variant="primary" $size="sm" onClick={handleLoadMore}>
            Загрузить ещё
          </Button>
        )}
        <span style={{ fontSize: "13px", color: theme.colors.textSecondary }}>
          Всего записей: {total}
        </span>
      </Pagination>
    </Container>
  );
};

export default AuditLog;
